import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm, useFieldArray } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import BuilderLayout from "../../Builder/BuilderLayout/BuilderLayout";
import BuilderTItle from "../../Builder/BuilderLayout/BuilderTitle";
import {
  CommonBuilderComponent,
  DeleteBuilderButton,
} from "../../Builder/Common/CommonBuilderComponent";
import PageDetails from "./PageDetails";
import FilterConfiguration from "./FilterConfiguration";
import StatusConfiguration from "./StatusConfiguration";
import DraggableSections from "./DraggableSections";
import AddSections from "./AddSections";
import SelectionSidePanel from "../../Builder/SelectionPanel/SelectionSidePanel";
import ModalForumDetailPage from "../../Builder/PreviewModal/ModalForumDetailPage";
import InternalPageLayout from "../../../../InternalPages/InternalPageLayout";
import { deleteAPI, getAPI, postAPI } from "../../../../../API/ApiService";
import { BASE_URL } from "../../../../../constant/apiUrl";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../utils/Toast/toast";
import ViewSummary from "../../../../ViewSummary/ViewSummary";
import { useSelector } from "react-redux";
import DeleteModal from "../../Builder/DeleteModal/DeleteModal";
import { setPage, setReset } from "../../../../../slices/adminPageSlice";
import { useDispatch } from "react-redux";
import { triggerRefetchNav } from "../../../../../slices/navSlice";
import Loading from "../../../../../utils/Loader";
import { Alert } from "@mui/material";

const validationSchema = yup.object().shape({
  title: yup.string().required("Page title is required"),
  sections: yup.array().of(
    yup.object().shape({
      sectionName: yup.string().required("Section name is required"),
    })
  ),
});

const CreatePageBuilderLayout = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectionId, setSelectSectionId] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [type, setType] = useState(null);
  const [status, setStatus] = useState(false);
  const [sectionRows, setSectionRows] = useState([]);
  const [filters, setFilters] = useState([]);
  const [description, setDescription] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [duplicate, setDuplicate] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [duplicateSection, setDuplicateSection] = useState(false);
  const [title, setTitle] = useState("");
  const [pageGroup, setPageGroup] = useState([]);
  const [deniedModal, setDeniedModal] = useState(false);
  const [deleteLoad, setDeleteLoad] = useState(false);
  const { viewSummaryDrawer } = useSelector((store) => store.viewSummary);
  const [loading, setLoading] = useState(true);
  const [navigationId, setNavigationid] = useState(null);

  const [isInvalid, setIsInvalid] = useState(false);

  const dispatch = useDispatch();
  const { internalGroups } = useSelector((store) => store.nav);
  const hasfetched = useRef();
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      title: "",
      sections: [], // initial sections
      description: "",
    },
  });

  const { handleSubmit, control, getValues, trigger } = methods;

  const { fields, append, remove, move, update } = useFieldArray({
    control,
    name: "sections",
  });

  useEffect(() => {
    // Auto-populate form on mount
    if (id) {
      const fetchData = async () => {
        try {
          const response = await getAPI(
            `${BASE_URL}/page-builder/find-by-page-id/${id}`
          );
          if (response?.status == 200) {
            const pageDetails = response?.data?.response?.page;
            const navigationDetails = response?.data?.response?.navigations;

            methods.reset({
              title: pageDetails?.name,
              sections: pageDetails?.pageSections?.map((s) => ({
                sectionId: s?.id,
                sectionName: s?.name,
                sectionDescription: s?.description,
                order: s?.sortOrder,
                id: s?.id,

                data:
                  s?.sectionContents?.map((item) => {
                    return {
                      ...item,
                      subject: item?.subjects,
                      description: item?.summary,
                    };
                  }) || [],
              })),
            });
            const newwparent = internalGroups
              .filter(
                (item) => item?.navigationId === navigationDetails?.parentId
              )
              .map((item) => {
                return {
                  displayText: item.label,
                  id: item.navigationId,
                };
              });
            setNavigationid(navigationDetails?.id);

            setStatus(pageDetails?.isActive);
            setPageGroup(newwparent);
            setDescription(navigationDetails?.description);
            setTitle(pageDetails?.name);
            const transformedFilters = pageDetails?.lovFilters.map((item) => ({
              id: crypto.randomUUID(), // generate unique id for the outer object
              selected: {
                id: item?.id,
                label: item?.label,
                description: `${item.label} Custom Filter`,
                active: true,
              },
            }));
            setFilters(transformedFilters);
          }
        } catch (e) {
          showErrorToast("Something went wrong");
        } finally {
          setLoading(false);
        }
      };
      if (!hasfetched.current) {
        hasfetched.current = true;
        fetchData();
      }
    } else {
      setLoading(false);
    }
  }, []);

  const handleBack = () => {
    dispatch(setPage("Page builder"));
    dispatch(setReset(false));
    navigate("/admin");
  };

  const handleSections = async () => {
    const isValid = await trigger("title");
    if (!isValid) return;

    append({
      sectionId: crypto.randomUUID(),
      sectionName: "",
      data: [],
      sectionDescription: "",
      order: fields.length,
    });
  };

  const handleSelection = (rows) => {
    if (!selectionId) return;

    const idx = fields.findIndex((f) => f.sectionId === selectionId);
    if (idx !== -1) {
      const currentValues = getValues(`sections.${idx}`);
      update(idx, {
        ...currentValues, // preserve all fields like sectionName & description
        sectionName: currentValues.sectionName,
        data: rows, // only update data
      });
    }

    setSectionRows(rows);
  };

  const mapToInternalPageLayout = (data, sections) => {
    const mapItemsByType = (items, type) =>
      items
        .filter((item) => item.type === type)
        .map((item) => ({
          ...item,
          thumbnailFileId: item?.thumbnailId || item.thumbnailFileId,
          uploadedOn: item?.createdDate,
          likes: item?.likeCount,
          description: item?.summary,
        }));

    return {
      ...data,
      header: data?.title,
      internalPageLovFilterItems: data?.filters.map((item) => ({
        label: item?.selected?.label,
        description: item?.selected?.label,
        id: item?.selected?.id,
      })),
      status: data?.status,
      internalPageSections: sections
        .sort((a, b) => a.order - b.order)
        .map((section, index) => ({
          sectionId: section?.sectionId || index,
          sectionName: section?.sectionName,
          sectionDescription: section?.sectionDescription,
          documents: mapItemsByType(section?.data || [], "DOCUMENT"),
          links: mapItemsByType(section?.data || [], "LINK"),
          videos: mapItemsByType(section?.data || [], "VIDEO"),
        })),
    };
  };

  const onSubmit = (data) => {
    const finalData = mapToInternalPageLayout(
      { ...data, status, filters, description },
      getValues("sections")
    );

    setPreviewData(finalData);
    setPreviewOpen(true);
  };

  const handlePublish = async () => {
    setIsPublishing(true);

    try {
      const payload = {
        page: {
          id: id ? Number(id) : null,
          name: previewData?.header,
          isActive: previewData?.status,
          lovFilterIds: previewData?.internalPageLovFilterItems?.map(
            (item) => item.id
          ),
          pageSections: previewData?.sections.map((item) => {
            return {
              name: item?.sectionName,
              contentIds: item?.data.map((item) => item.contentId || item.id),
              isActive: true,
              description: item?.sectionDescription,
            };
          }),
        },
        navigations: {
          name: previewData.header,
          pageId: id ? Number(id) : null,
          description,
          parentId: pageGroup?.[0]?.id ?? null,
          id: navigationId ? Number(navigationId) : null,
          type: "INTERNAL",
          isActive: status,
        },
      };

      const response = await postAPI(
        `${BASE_URL}/page-builder/persist`,
        payload
      );

      if (response?.status === 200) {
        showSuccessToast(
          previewData.status
            ? "Page published successfully!"
            : "Page saved successfully!"
        );
        handleBack();
        dispatch(triggerRefetchNav(true));
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsPublishing(false);
      setPreviewOpen(false);
    }
  };

  const watchedSections = methods.watch("sections");

  // Compute existing section based on selectionId
  const existing = watchedSections?.find((s) => s.sectionId === selectionId);

  const config = { documentFlag: true, videoFlag: true, linkFlag: true };
  const handleDelete = async () => {
    try {
      setDeleteLoad(true);
      const data = await deleteAPI(
        `${BASE_URL}/page-builder/delete/${Number(id)}`
      );
      if (data?.status === 200) {
        showSuccessToast("Page Deleted Successfully");
        dispatch(triggerRefetchNav(true));
        handleBack();
      }
    } catch (e) {
      showErrorToast("Failed To Delete Forum");
    } finally {
      setDeleteLoad(false);
    }
  };
  return (
    <BuilderLayout
      label={id ? "Save Page" : "Create page"}
      form="page-builder-form"
      backlabel="Back to page list"
      back={handleBack}
      disabled={duplicate || duplicateSection || isInvalid}
    >
      {loading && <Loading />}
      {deniedModal && (
        <DeleteModal
          title={"Delete Page"}
          setClose={() => setDeniedModal(false)}
          open={deniedModal}
          handleDelete={handleDelete}
          text={
            "You are about to delete the page that is published in the site. Please confirm"
          }
          loading={deleteLoad}
        />
      )}
      <ModalForumDetailPage
        forumModal={previewOpen}
        setForumModal={setPreviewOpen}
        handlePublish={handlePublish}
        isPublishing={isPublishing}
        status={status}
        text={`${status ? "Publish page" : "Save page"}`}
      >
        <InternalPageLayout
          pageData={previewData}
          header={previewData?.header || "Preview Header"}
          slug="preview-slug"
          nodata={false}
          cardButtonDisable={true}
        />
      </ModalForumDetailPage>
      {viewSummaryDrawer && <ViewSummary />}
      <FormProvider {...methods}>
        <form
          id="page-builder-form"
          data-testid="page-builder"
          onSubmit={handleSubmit(onSubmit)}
        >
          <SelectionSidePanel
            open={openModal}
            setOpen={setOpenModal}
            selectedItems={existing?.data}
            handleRowSelection={handleSelection}
            flagConfig={config}
          />

          <div className="flex flex-col gap-4">
            <CommonBuilderComponent>
              <div className="flex flex-col gap-4 w-full">
                <BuilderTItle
                  title="Page details"
                  rightElement={
                    id && (
                      <DeleteBuilderButton
                        label={"Delete page"}
                        onClick={() => {
                          setDeniedModal(true);
                        }}
                      />
                    )
                  }
                />
                <PageDetails
                  setDescription={setDescription}
                  setDuplicate={setDuplicate}
                  duplicate={duplicate}
                  title={title}
                  setTitle={setTitle}
                  description={description}
                  pageGroup={pageGroup}
                  setPageGroup={setPageGroup}
                  id={id}
                  navigationId={navigationId}
                />
                <FilterConfiguration
                  filters={filters}
                  setFilters={setFilters}
                  setIsInvalid={setIsInvalid}
                />
                {isInvalid && (
                  <Alert severity="error" sx={{ mt: 1, maxWidth: "350px" }}>
                    Please select a value for all filters.
                  </Alert>
                )}
                <StatusConfiguration status={status} setStatus={setStatus} />
              </div>
            </CommonBuilderComponent>

            <DraggableSections
              setOpenModal={setOpenModal}
              sectionRows={sectionRows}
              setSelectSectionId={setSelectSectionId}
              selectionId={selectionId}
              type={type}
              setType={setType}
              fields={fields}
              remove={remove}
              move={move}
              duplicateSection={duplicateSection}
              setDuplicateSection={setDuplicateSection}
            />

            <div className="flex justify-end">
              <AddSections onClick={handleSections} label="Add Sections" />
            </div>
          </div>
        </form>
      </FormProvider>
    </BuilderLayout>
  );
};

export default CreatePageBuilderLayout;
